import { useState } from 'react';
import { X, FileText, Download, Printer, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { CustodyChainEvent } from '../../api/client';
import { exportAuditReportPdf, exportAuditReportCsv } from '../../utils/exportPdf';
import { useAuth } from '../../contexts/AuthContext';

interface AuditReportPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  events: CustodyChainEvent[];
  filters: {
    dateRange?: { from: string; to: string };
    eventType?: string;
    user?: string;
    caseNumber?: string;
  };
  stats: {
    totalEvents: number;
    byEventType: Record<string, number>;
  };
}

export function AuditReportPreview({
  isOpen,
  onClose,
  events,
  filters,
  stats,
}: AuditReportPreviewProps) {
  const { user } = useAuth();
  const [reportTitle, setReportTitle] = useState('DEMS Audit Report');

  if (!isOpen) return null;

  const handleExportPdf = () => {
    exportAuditReportPdf({
      title: reportTitle,
      generatedBy: user?.name || 'System Administrator',
      generatedAt: new Date(),
      filters,
      events,
      summary: stats,
    });
  };

  const handleExportCsv = () => {
    exportAuditReportCsv(events, `${reportTitle.replace(/\s+/g, '-').toLowerCase()}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  // Count events by type for preview
  const eventTypeCounts = events.reduce((acc, event) => {
    acc[event.eventType] = (acc[event.eventType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Export Audit Report</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Report Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Title
            </label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Preview Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Preview Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Eye className="h-4 w-4" />
                Report Preview
              </div>
            </div>

            {/* Preview Content */}
            <div className="p-4 bg-white">
              {/* Report Header Preview */}
              <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-primary-900">{reportTitle}</h3>
                  <p className="text-sm text-gray-500">Chain of Custody Audit Report</p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p className="font-medium text-primary-900">DEMS</p>
                  <p>Digital Evidence Management System</p>
                </div>
              </div>

              {/* Report Metadata */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Generated By</p>
                  <p className="text-sm font-medium">{user?.name || 'System Administrator'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Generated At</p>
                  <p className="text-sm font-medium">{format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
                </div>
                {filters.dateRange && (
                  <div>
                    <p className="text-xs text-gray-500">Date Range</p>
                    <p className="text-sm font-medium">{filters.dateRange.from} to {filters.dateRange.to}</p>
                  </div>
                )}
                {filters.eventType && (
                  <div>
                    <p className="text-xs text-gray-500">Event Type Filter</p>
                    <p className="text-sm font-medium capitalize">{filters.eventType.replace('_', ' ')}</p>
                  </div>
                )}
              </div>

              {/* Summary Stats */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500">Total Events</p>
                    <p className="text-xl font-semibold text-primary-900">{events.length}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600">Uploads</p>
                    <p className="text-xl font-semibold text-green-700">{eventTypeCounts.upload || 0}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600">Downloads</p>
                    <p className="text-xl font-semibold text-blue-700">{eventTypeCounts.download || 0}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-600">Integrity</p>
                    <p className="text-xl font-semibold text-purple-700">{eventTypeCounts.integrity_check || 0}</p>
                  </div>
                </div>
              </div>

              {/* Events Preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Events Preview ({Math.min(events.length, 5)} of {events.length})
                </h4>
                <div className="border border-gray-200 rounded overflow-hidden">
                  <table className="min-w-full text-xs">
                    <thead className="bg-primary-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Timestamp</th>
                        <th className="px-3 py-2 text-left">Event</th>
                        <th className="px-3 py-2 text-left">User</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {events.slice(0, 5).map((event, i) => (
                        <tr key={event.id} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="px-3 py-2">{format(new Date(event.performedAt), 'MMM d, h:mm a')}</td>
                          <td className="px-3 py-2 capitalize">{event.eventType.replace('_', ' ')}</td>
                          <td className="px-3 py-2">{event.performedByName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {events.length > 5 && (
                    <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                      ... and {events.length - 5} more events
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {events.length} events will be included in the report
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={handleExportPdf}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <Printer className="h-4 w-4" />
              Print / PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuditReportPreview;
